import * as React from 'react'
import { ReactElement, useContext, useEffect, useState } from 'react'
import { UnitDto } from '../../../../context/GlobalContext'
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import EntitySearchResultDialog from './EntitySearchResultDialog'
import EditableEntityInput, { StatusIcon } from './EditableEntityInput'
import useAxios from '../../../../hooks/useAxios'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import EntityLabel, { EntityLabelType } from './EntityLabel'
import { AccountingId } from '../../../../config/finance/invoiceConfig'
import { useTranslation } from 'react-i18next'
import { AddDimensionValues } from './CreateDimensionDialog'
import AuthorizationContext from '../../../../context/AuthorizationContext'

enum SearchType {
    Code = 'CODE',
    Name = 'NAME',
}

export type SearchResult = {
    id: string
    code: string
    name: string
}

export type CreateDialogProps = {
    onCreate: (item: SearchResult) => void
}

export enum EntityType {
    // noinspection JSUnusedGlobalSymbols
    CostCenter = 'cost-center',
    CostUnit = 'cost-unit',
    Customer = 'customer',
    GLA = 'gla',
    Project = 'project',
    Transaction = 'transaction',
}

export type EntityProps = {
    unit: UnitDto
    labelType: EntityLabelType
    externalType: EntityType
    value?: AccountingId
    setFieldValue: (item: AccountingId) => void
    initialValues?: AddDimensionValues
    createDialog?: (props: CreateDialogProps) => ReactElement<CreateDialogProps>
    readOnly?: boolean
}

const FieldNameMap: Map<SearchType, keyof SearchResult> = new Map([
    [SearchType.Code, 'code'],
    [SearchType.Name, 'name'],
])

const getValue = (item: SearchResult, searchType: SearchType): string => item[FieldNameMap.get(searchType)]

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXS,
    },
    labelRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalXS,
    },
    labelGrow: {
        flexGrow: 1,
    },
    fields: {
        display: 'flex',
        columnGap: tokens.spacingHorizontalM,
        rowGap: tokens.spacingVerticalXS,
    },
    fieldsHorizontal: {
        flexDirection: 'row',
    },
    fieldsVertical: {
        flexDirection: 'column',
    },
    codeField: {
        maxWidth: '160px',
    },
    nameField: {
        flexGrow: 1,
    },
})

type FormFieldProps = EntityProps & {
    code: string
    setCode: (code: string) => void
    name: string
    setName: (name: string) => void
    initialName: string
    statusIcon: StatusIcon
    save: (searchType: SearchType, prevValue: string) => (value: string) => Promise<StatusIcon>
}

function FormFields(props: FormFieldProps) {
    const { t: translate } = useTranslation()
    const { isViewport } = useViewport()
    const styles = useStyles()

    const horizontal = isViewport('>=', Viewport.MainMenu)

    return (
        <div className={mergeClasses(styles.fields, horizontal ? styles.fieldsHorizontal : styles.fieldsVertical)}>
            <EditableEntityInput
                className={horizontal ? styles.codeField : undefined}
                readOnly={props.readOnly}
                prefix="#"
                statusIcon={props.code ? props.statusIcon : StatusIcon.None}
                value={props.code}
                onFormat={value => {
                    props.setName(value.trim() === props.value?.code ? props.initialName : '')
                    return value
                }}
                save={props.save(SearchType.Code, props.code)}
            />
            <EditableEntityInput
                className={styles.nameField}
                readOnly={props.readOnly}
                prefix={translate('base.table.header.name')}
                value={props.name}
                statusIcon={props.name ? props.statusIcon : StatusIcon.None}
                onFormat={value => {
                    props.setCode(value.trim() === props.initialName ? props.value?.code : '')
                    return value
                }}
                save={props.save(SearchType.Name, props.name)}
            />
        </div>
    )
}

export default function EntityField(props: EntityProps): ReactElement {
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const styles = useStyles()

    const [id, setId] = useState<string>(props.value?.id)
    const [code, setCode] = useState<string>(props.value?.code)
    const [name, setName] = useState<string>(props.value?.name)
    const [initialName, setInitialName] = useState<string>(props.value?.name)
    const [statusIcon, setStatusIcon] = useState<StatusIcon>(props.value?.id ? StatusIcon.Success : StatusIcon.None)
    const [searchResults, setSearchResults] = useState<SearchResult[]>()

    const setItem = (item: SearchResult, statusIcon: StatusIcon = StatusIcon.None): Promise<StatusIcon> => {
        if (statusIcon === StatusIcon.Success || !item) {
            props.setFieldValue(item ? { ...item, unit: props.unit.key } : undefined)
        }
        setInitialName(item?.name || '')
        setId(item?.id || '')
        setCode(item?.code || '')
        setName(item?.name || '')
        setStatusIcon(statusIcon)
        setSearchResults(undefined)
        return Promise.resolve(statusIcon)
    }

    const doSearch = (searchType: SearchType, value: string, strict?: boolean): Promise<SearchResult[]> =>
        axios
            .get<SearchResult[]>(`/accounting/search/${props.externalType}/${currentHost?.RowKey}/${props.unit.key}`, {
                params: { query: value },
                baseURL: process.env.API_HOST,
                headers: { SuppressErrors: 'true' },
            })
            .then(response =>
                response.data.filter(item => !strict || getValue(item, searchType) === value).length === 0
                    ? Promise.reject()
                    : Promise.resolve(response.data),
            )
            .catch(() => {
                setItem(
                    {
                        id: '',
                        code: searchType === SearchType.Code ? value : '',
                        name: searchType === SearchType.Name ? value : '',
                    },
                    StatusIcon.Failed,
                )
                return Promise.reject()
            })

    const save =
        (searchType: SearchType, prevValue: string): ((value: string) => Promise<StatusIcon>) =>
        (value: string) => {
            if (prevValue === value.trim()) {
                return setItem(
                    {
                        id: id,
                        code: searchType === SearchType.Code ? value : code,
                        name: searchType === SearchType.Name ? value : name,
                    },
                    value.trim().length === 0 ? StatusIcon.None : statusIcon,
                )
            } else if (value.trim().length === 0) {
                return setItem(null, StatusIcon.None)
            }

            return doSearch(searchType, value, searchType === SearchType.Code)
                .then(results => {
                    if (results.length === 1) {
                        return setItem(results.values().next().value, StatusIcon.Success)
                    } else {
                        setId('')
                        setCode(searchType === SearchType.Code ? value : '')
                        setName(searchType === SearchType.Name ? value : '')
                        setSearchResults(results)
                        return Promise.resolve(StatusIcon.Saving)
                    }
                })
                .catch(() => Promise.resolve(StatusIcon.Failed))
        }

    useEffect(() => {
        // Backwards compatible check for empty name
        if (props.value?.code !== code || props.value?.name === undefined) {
            if (props.value?.code) {
                setCode(props.value?.code)
                doSearch(SearchType.Code, props.value?.code, true)
                    .then(results => {
                        return setItem(results.values().next().value, StatusIcon.Success)
                    })
                    .catch(() => ({}))
            } else {
                setName('')
            }
        }
    }, [props.value])

    return (
        <div className={styles.root}>
            <div className={styles.labelRow}>
                {props.createDialog && props.initialValues && <props.createDialog onCreate={item => setItem(item, StatusIcon.Success)} />}
                <div className={styles.labelGrow}>
                    <EntityLabel unit={props.unit} externalType={props.externalType} labelType={props.labelType} />
                </div>
            </div>
            <FormFields
                {...props}
                code={code}
                setCode={setCode}
                name={name}
                setName={setName}
                initialName={initialName}
                statusIcon={statusIcon}
                save={save}
            />
            <EntitySearchResultDialog
                items={searchResults}
                externalType={props.externalType}
                onSelect={item => setItem(item, StatusIcon.Success)}
                onDismiss={() => setItem(undefined)}
            />
        </div>
    )
}
